import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  LinearProgress,
  Stack,
  Avatar,
  CircularProgress,
  Fade,
  Zoom,
} from "@mui/material";

import {
  CheckCircleRounded,
  ErrorRounded,
  WarningAmberRounded,
  HourglassTopRounded,
  SettingsRounded,
} from "@mui/icons-material";

/*
STATUS TYPES:
success
loading
warning
pending
error
*/

//  Updated: Status Map now contains Light and Dark mode color profiles
const statusMap = {
  success: {
    light: { bg: "#ecfdf3", border: "#bbf7d0", title: "#166534", desc: "#166534" },
    dark: { bg: "rgba(22, 163, 74, 0.1)", border: "rgba(22, 163, 74, 0.2)", title: "#4ade80", desc: "#86efac" },
    iconBg: "#16a34a",
    icon: <CheckCircleRounded />,
  },

  loading: {
    light: { bg: "#eff6ff", border: "#bfdbfe", title: "#1e3a8a", desc: "#475569" },
    dark: { bg: "rgba(37, 99, 235, 0.1)", border: "rgba(37, 99, 235, 0.2)", title: "#93c5fd", desc: "#bfdbfe" },
    iconBg: "#2563eb",
    icon: <CircularProgress size={18} thickness={5} color="inherit" />,
  },

  warning: {
    light: { bg: "#fff7ed", border: "#fed7aa", title: "#92400e", desc: "#92400e" },
    dark: { bg: "rgba(245, 158, 11, 0.1)", border: "rgba(245, 158, 11, 0.2)", title: "#fcd34d", desc: "#fde68a" },
    iconBg: "#f59e0b",
    icon: <WarningAmberRounded />,
  },

  pending: {
    light: { bg: "#f8fafc", border: "#cbd5e1", title: "#334155", desc: "#475569" },
    dark: { bg: "rgba(100, 116, 139, 0.1)", border: "rgba(100, 116, 139, 0.2)", title: "#cbd5e1", desc: "#94a3b8" },
    iconBg: "#64748b",
    icon: <HourglassTopRounded />,
  },

  error: {
    light: { bg: "#fef2f2", border: "#fecaca", title: "#991b1b", desc: "#991b1b" },
    dark: { bg: "rgba(220, 38, 38, 0.1)", border: "rgba(220, 38, 38, 0.2)", title: "#fca5a5", desc: "#fecaca" },
    iconBg: "#dc2626",
    icon: <ErrorRounded />,
  },
};

const StatusCard = ({
  title,
  description,
  status,
  delay = 0,
}) => {
  const cfg = statusMap[status] || statusMap.pending;

  return (
    <Zoom in timeout={400 + delay}>
      <Box
        sx={{
          p: 2,
          mb: 1.8,
          borderRadius: 3,
          //  Dynamically fetch the nested dark/light color based on theme
          background: (t) => t.palette.mode === 'dark' ? cfg.dark.bg : cfg.light.bg,
          border: (t) => `1px solid ${t.palette.mode === 'dark' ? cfg.dark.border : cfg.light.border}`,
          boxShadow: (t) => t.palette.mode === 'dark' ? "none" : "0 8px 24px rgba(15,23,42,0.04)",
          transition: "all .3s ease",
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
        >
          <Avatar
            sx={{
              width: 44,
              height: 44,
              bgcolor: cfg.iconBg,
              color: "#fff"
            }}
          >
            {cfg.icon}
          </Avatar>

          <Box sx={{ flex: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                color: (t) => t.palette.mode === 'dark' ? cfg.dark.title : cfg.light.title,
                lineHeight: 1.2,
              }}
            >
              {title}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mt: 0.5,
                color: (t) => t.palette.mode === 'dark' ? cfg.dark.desc : cfg.light.desc,
                lineHeight: 1.45,
              }}
            >
              {description}
            </Typography>
          </Box>
        </Stack>
      </Box>
    </Zoom>
  );
};

const SubmissionProgressPopup = ({
  open,
  progress = 0,
  steps = {},
  successMessage
}) => {
  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "24px",
          overflow: "hidden",
          //  Adapts the main dialog background
          background: (t) => t.palette.mode === 'dark' ? t.palette.background.paper : "linear-gradient(180deg,#ffffff 0%,#f8fafc 100%)",
          boxShadow: (t) => t.palette.mode === 'dark' ? "0 30px 80px rgba(0,0,0,.6)" : "0 30px 80px rgba(15,23,42,.18)",
        },
      }}
    >
      <DialogContent sx={{ p: 3 }}>
        {/* HEADER */}
        <Fade in timeout={500}>
          <Stack
            spacing={1.4}
            alignItems="center"
            sx={{ mb: 3 }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
                boxShadow: "0 20px 35px rgba(99,102,241,.25)",
              }}
            >
              <SettingsRounded
                sx={{
                  color: "#fff",
                  fontSize: 34,
                }}
              />
            </Box>

            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                color: "text.primary", // Swapped to native text variable
                textAlign: "center",
              }}
            >
              Processing Request
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "text.secondary", // Swapped to native text variable
                textAlign: "center",
                maxWidth: 420,
              }}
            >
              Please wait while we validate
              integrations and create your
              cloud resource request.
            </Typography>
          </Stack>
        </Fade>

        {/* PROGRESS */}
        <Box sx={{ mb: 3 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ mb: 1 }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "text.secondary",
              }}
            >
              Overall Progress
            </Typography>

            <Typography
              variant="caption"
              sx={{
                fontWeight: 800,
                color: "#4f46e5",
              }}
            >
              {progress}%
            </Typography>
          </Stack>

          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 10,
              borderRadius: 999,
              //  Adapt the unfilled background line
              backgroundColor: (t) => t.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0",
              "& .MuiLinearProgress-bar": {
                borderRadius: 999,
                background: "linear-gradient(90deg,#6366f1,#8b5cf6)",
              },
            }}
          />
        </Box>

        {/* STEP 1 */}
        <StatusCard
          title="Integration Status"
          description={
            steps.integration?.desc ||
            "Waiting to start..."
          }
          status={
            steps.integration?.status ||
            "pending"
          }
          delay={0}
        />

        {/* STEP 2 */}
        <StatusCard
          title="Resource Creation"
          description={
            steps.provisioning?.desc ||
            "Waiting to start..."
          }
          status={
            steps.provisioning?.status ||
            "pending"
          }
          delay={150}
        />

        {/* SUCCESS MESSAGE */}
        {successMessage && steps.provisioning?.status === "success" && (
          <Fade in timeout={600}>
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 3,
                //  Adapts to match the success card logic
                background: (t) => t.palette.mode === 'dark' ? "rgba(22, 163, 74, 0.1)" : "#ecfdf3",
                border: (t) => `1px solid ${t.palette.mode === 'dark' ? "rgba(22, 163, 74, 0.2)" : "#bbf7d0"}`,
                textAlign: "center"
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  color: (t) => t.palette.mode === 'dark' ? "#4ade80" : "#166534"
                }}
              >
                {successMessage}
              </Typography>
            </Box>
          </Fade>
        )}

        {/* FOOTER NOTE */}
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 1,
            textAlign: "center",
            color: "text.disabled", // Swapped to native text variable
          }}
        >
          Please do not close this window while
          processing.
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default SubmissionProgressPopup;